import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { auth } from '../lib/auth'
import { useTheme } from '../lib/theme-provider'
import { ThemedCard } from './ThemedCard'
import { ThemedButton } from './ThemedButton'

interface NicknameLoginModalProps {
  isOpen: boolean
  onClose: () => void
  onLogin: (nickname: string, avatar: string) => void
  onLogout?: () => void
}

export default function NicknameLoginModal({ isOpen, onClose, onLogin, onLogout }: NicknameLoginModalProps) {
  const { theme } = useTheme()
  const [nickname, setNickname] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setNickname('')
      setError('')
    }
  }, [isOpen])

  const handleLogin = async () => {
    if (!nickname.trim()) {
      setError('请输入昵称')
      return
    }

    if (nickname.trim().length < 1) {
      setError('昵称至少 1 个字符')
      return
    }

    if (nickname.trim().length > 32) {
      setError('昵称不能超过 32 个字符')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const { error: rpcError } = await supabase.rpc('get_or_create_game_user', {
        p_nickname: nickname.trim()
      })

      if (rpcError) throw rpcError

      auth.login(nickname.trim(), '🧙‍♂️')
      onLogin(nickname.trim(), '🧙‍♂️')
    } catch (err) {
      console.error('Login error:', err)
      setError('登录失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin()
    }
  }

  const handleClose = () => {
    if (onLogout) {
      onLogout()
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-sm p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold" style={{ color: theme.colors['text-primary'] }}>
            登录账号
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg transition-colors"
            style={{ 
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors['bg-primary']
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <X size={20} style={{ color: theme.colors['text-secondary'] }} />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <label 
            className="block text-sm font-medium mb-2"
            style={{ color: theme.colors['text-secondary'] }}
          >
            昵称
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="请输入 1-32 个字符的昵称"
            maxLength={32}
            autoFocus
            className="w-full px-4 py-3 rounded-xl outline-none transition-colors"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              border: `2px solid ${theme.colors['border-secondary']}`,
              color: theme.colors['text-primary'],
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = theme.colors['accent-gold']
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = theme.colors['border-secondary']
            }}
          />

          {error && (
            <p className="text-sm mt-2" style={{ color: theme.colors['accent-red'] }}>
              {error}
            </p>
          )}

          {/* 重要提醒 */}
          <div 
            className="mt-5 p-4 rounded-xl border-2"
            style={{
              backgroundColor: `${theme.colors['accent-gold']}10`,
              borderColor: `${theme.colors['accent-gold']}40`,
            }}
          >
            <p 
              className="text-sm font-bold mb-2 flex items-center gap-2"
              style={{ color: theme.colors['accent-gold'] }}
            >
              <span className="text-lg">⚠️</span>
              重要提醒
            </p>
            <p 
              className="text-xs leading-relaxed font-medium"
              style={{ color: theme.colors['accent-gold'] }}
            >
              该昵称将用于同步线上游戏和战绩数据，请确保与之前使用的昵称一致！
            </p>
          </div>
        </div>

        {/* Action Button */}
        <ThemedButton
          onClick={handleLogin}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? '登录中...' : '开始游戏'}
        </ThemedButton>
      </ThemedCard>
    </div>
  )
}
